import { useEffect, useState } from 'react'
import { CreditCard, Loader2, ArrowLeft } from 'lucide-react'
import { billingService, VAT_RATE, type BillingProfile } from '@core/billing'

interface CheckoutPanelProps {
  orderId: string
  /** Net total (euros) from the picker, ex-IVA. */
  netEur: number
  productCount: number
  /** Leave checkout without paying (back to the order view). */
  onCancel: () => void
}

const emptyBilling: BillingProfile = {
  nif: '',
  billing_name: '',
  address: '',
  city: '',
  postal_code: '',
  country: 'PT',
}

/**
 * Checkout step (Epic H, Phase 1): review (net + IVA + total) + billing/NIF
 * form, then redirect to the hosted Stripe Checkout. Rendered by OrderPanel
 * after an order is created while payments are enabled.
 */
export function CheckoutPanel({ orderId, netEur, productCount, onCancel }: CheckoutPanelProps) {
  const [billing, setBilling] = useState<BillingProfile>(emptyBilling)
  const [loading, setLoading] = useState(true)
  const [paying, setPaying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    billingService.getBillingProfile().then(({ profile }) => {
      if (!cancelled && profile) setBilling({ ...emptyBilling, ...profile, country: profile.country || 'PT' })
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  const vat = netEur * VAT_RATE
  const total = netEur + vat
  const set = (k: keyof BillingProfile) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setBilling((b) => ({ ...b, [k]: e.target.value }))

  const pay = async () => {
    if (!billing.nif?.trim()) { setError('Indique o NIF de faturacao.'); return }
    setPaying(true)
    setError(null)
    const { session, error: e } = await billingService.createCheckoutSession(orderId, billing)
    if (e || !session) {
      setError(e?.message ?? 'Erro ao iniciar o pagamento.')
      setPaying(false)
      return
    }
    window.location.assign(session.url) // hosted Stripe Checkout
  }

  return (
    <div
      className="absolute top-14 right-0 z-20 h-[calc(100%-3.5rem)] w-[360px] max-w-full
                 bg-basalt/90 backdrop-blur-md border-l border-white/[0.1]
                 flex flex-col text-limestone"
    >
      <div className="flex items-center justify-between px-4 h-12 border-b border-white/[0.08] shrink-0">
        <span className="text-sm font-medium">Pagamento</span>
        <button type="button" onClick={onCancel} className="text-clay hover:text-limestone p-1 flex items-center gap-1 text-xs">
          <ArrowLeft className="w-4 h-4" /> mais tarde
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Review */}
        <div className="rounded-lg border border-white/[0.1] bg-white/[0.04] p-3 space-y-1.5 text-sm">
          <div className="flex justify-between text-clay">
            <span>{productCount} produto{productCount !== 1 ? 's' : ''}</span>
            <span>{netEur.toFixed(2)} &euro;</span>
          </div>
          <div className="flex justify-between text-clay">
            <span>IVA ({Math.round(VAT_RATE * 100)}%)</span>
            <span>{vat.toFixed(2)} &euro;</span>
          </div>
          <div className="flex justify-between font-semibold pt-1.5 border-t border-white/[0.08]">
            <span>Total</span>
            <span>{total.toFixed(2)} &euro;</span>
          </div>
        </div>

        {/* Billing identity */}
        <div className="space-y-2">
          <label className="block text-xs text-clay">NIF *</label>
          <input
            value={billing.nif ?? ''}
            onChange={set('nif')}
            inputMode="numeric"
            placeholder="contribuinte"
            className="w-full rounded-lg px-3 py-2 text-sm bg-schist border border-white/[0.1] outline-none focus:border-terracotta/60"
          />
          <label className="block text-xs text-clay">Nome / entidade</label>
          <input
            value={billing.billing_name ?? ''}
            onChange={set('billing_name')}
            placeholder="nome para a fatura"
            className="w-full rounded-lg px-3 py-2 text-sm bg-schist border border-white/[0.1] outline-none focus:border-terracotta/60"
          />
          <label className="block text-xs text-clay">Morada</label>
          <input
            value={billing.address ?? ''}
            onChange={set('address')}
            placeholder="morada"
            className="w-full rounded-lg px-3 py-2 text-sm bg-schist border border-white/[0.1] outline-none focus:border-terracotta/60"
          />
          <div className="flex gap-2">
            <input
              value={billing.postal_code ?? ''}
              onChange={set('postal_code')}
              placeholder="codigo postal"
              className="w-1/2 rounded-lg px-3 py-2 text-sm bg-schist border border-white/[0.1] outline-none focus:border-terracotta/60"
            />
            <input
              value={billing.city ?? ''}
              onChange={set('city')}
              placeholder="localidade"
              className="w-1/2 rounded-lg px-3 py-2 text-sm bg-schist border border-white/[0.1] outline-none focus:border-terracotta/60"
            />
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={pay}
          disabled={paying || loading}
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-terracotta text-limestone px-3 py-2.5 text-sm font-medium disabled:opacity-40"
        >
          {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
          {paying ? 'A redirecionar...' : `Pagar ${total.toFixed(2)} €`}
        </button>
        <p className="text-[11px] text-clay leading-snug">
          Pagamento seguro via Stripe. O processamento do pedido inicia apos a confirmacao do pagamento.
        </p>
      </div>
    </div>
  )
}
